<script setup>
    import { computed, ref, watch } from 'vue'
    import { useEditStateStore } from '@/js/stores/editState'

    const props = defineProps({
        entry: {
            type: String,
            required: true
        },
        editKey: {
            type: String,
            required: true
        },
        autoCompleteData: {
            type: Array,
            required: false,
            default: () => []
        },
        title: {
            type: String,
            default: ''
        },
        toolTip: {
            type: String,
            default: ''
        },
        showTitle: {
            type: Boolean,
            required: false,
            default: true
        },
        allowEdit: {
            type: Boolean,
            required: false,
            default: false
        }
    })
    const emit = defineEmits(['textSaved', 'editAborted', 'editStarted'])

    const editStore = useEditStateStore()
    watch(
        () => props.editKey,
        (newKey) => {
            editStore.register([newKey])
        },
        { immediate: true }
    )
    const editEntry = ref('')

    const isLoading = computed(() => store.getters['isLoading'])
    const isEditing = computed(() => editStore.editStates[props.editKey])

    function startEditing() {
        editStore.set(props.editKey, true)
        editEntry.value = props.entry
    }

    function stopEditing() {
        editStore.set(props.editKey, false)
        editEntry.value = ''
    }

    const isSaveButtonDisabled = computed(() => isLoading.value) // || props.entry === editEntry);

    const entryLabel = computed(() => {
        return props.entry || '-'
    })

    function handleEditClick() {
        if (isLoading.value) {
            return
        }
        emit('editStarted')
        startEditing()
    }

    function handleAbortClick() {
        if (isLoading.value) {
            return
        }
        emit('editAborted')
        stopEditing()
    }

    async function handleSaveClick() {
        if (isLoading.value) {
            return
        }

        // emit changed values to parent component or store
        // should emit iri or ids
        emit('textSaved', editEntry.value)
        stopEditing()
    }
</script>

<template>
    <v-form novalidate @submit.prevent>
        <div class="d-flex w-100 flex-nowrap align-stretch flex-grow-1">
            <common-input-header :title="title" :show-title="showTitle" :tool-tip="toolTip" />
            <div
                v-if="isEditing"
                class="d-flex align-center input-entry-edit flex-grow-1 border-primary border border-opacity-100"
                style="min-width: 0"
            >
                <slot name="edit-component">
                    <CommonTextField v-model="editEntry" />
                </slot>
            </div>
            <div
                v-else
                class="d-flex align-center pa-2 input-entry-show flex-grow-1 border-primary border border-opacity-100 rich-text"
                style="min-width: 0"
            >
                <slot name="preview-component">
                    {{ entryLabel }}
                </slot>
            </div>
            <template v-if="allowEdit">
                <v-btn
                    v-if="!isEditing"
                    icon="mdi-lead-pencil"
                    border
                    height="inherit"
                    class="border-primary border-opacity-100 text-grey"
                    color="primary-lighten-2"
                    tile
                    variant="flat"
                    @click="handleEditClick"
                />
                <v-btn
                    v-if="isEditing"
                    icon="mdi-check"
                    :disabled="isSaveButtonDisabled"
                    border
                    height="inherit"
                    class="border-primary border-opacity-100 text-grey"
                    color="primary-lighten-2"
                    tile
                    variant="flat"
                    @click="handleSaveClick"
                />
            </template>
            <v-btn
                v-if="allowEdit && isEditing"
                icon="mdi-cancel"
                border
                height="inherit"
                class="border-primary border-opacity-100 text-grey"
                color="primary-lighten-2"
                tile
                variant="flat"
                @click="handleAbortClick"
            />
        </div>
    </v-form>
</template>

<script></script>

<style scoped lang="scss"></style>
<style lang="scss">
    input::-webkit-calendar-picker-indicator {
        display: none;
        opacity: 0;
    }
    .input-entry-show {
        white-space: pre-line;
    }
    .input-entry-edit {
        min-width: 15rem;
    }
    .rich-text ul,
    .rich-text ol {
        padding-inline-start: 1.5rem;
        margin-block: 0.5rem;
    }

    .rich-text li {
        margin-block: 0.25rem;
    }
</style>
